import React, { useState } from 'react'
import { Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Tooltip, Typography } from '@mui/material'
import { globalGetService, globalPostService, globalPutService } from '../../../utils/globalApiServices'
import { checkApiStatus } from '../../../utils'
import { enqueueSnackbar } from 'notistack'
import RestoreIcon from '@mui/icons-material/Restore'
import { getApprovalWorkingGroupsDropdownApi, sendInvoiceForApprovalApi, invoiceApprovalActionApi, getApprovalFlowimelineApi } from '../apiService'
import { trackActivity } from '../../../utils/mixpanel'
import { useDispatch, useSelector } from 'react-redux'
import { AttachementsUpload } from '../../../shared_elements/components'
import { getStorage } from '../../../shared_elements/actionCreators'
import fileUpload, { formatBytes } from '../../../utils/fileUpload'
import ApprovalTimeline from './ApprovalTimeline'

// UI status codes (what drives button labels / disabled state)
const STATUS = { NOT_ENABLED: 0, DRAFT: 1, PENDING: 2, APPROVED: 3, REJECTED: 4 }
// API member status codes returned by backend
const MEMBER_STATUS = { PENDING: 1, APPROVED: 2, REJECTED: 3 }

const InvoiceApprovalFlow = ({ invoiceSlug, leaseSlug, approvalInfo, getApprovalDetails, invoiceStatus, getInvoice }) => {
    const dispatch = useDispatch()
    const { storageDetails } = useSelector(state => state.leases)

    const [workingGroups, setWorkingGroups] = useState([])
    const [groupsLoading, setGroupsLoading] = useState(false)
    // Send-for-approval dialog
    const [sendDialog, setSendDialog] = useState(false)
    const [selectedGroup, setSelectedGroup] = useState(null)
    const [groupError, setGroupError] = useState('')
    const [sendLoading, setSendLoading] = useState(false)
    // Approve / Reject dialog
    const [actionDialog, setActionDialog] = useState(false)
    const [remark, setRemark] = useState('')
    const [remarkError, setRemarkError] = useState('')
    const [actionLoading, setActionLoading] = useState(null) // 'approve' | 'reject'
    const [attachments, setAttachments] = useState([])
    // Revision history
    const [timeLineData, setTimeLineData] = useState({ modal: false, data: [] })
    
    const fetchWorkingGroups = () => {
        setGroupsLoading(true)
        globalGetService(getApprovalWorkingGroupsDropdownApi(leaseSlug)).then(response => {
            setGroupsLoading(false)
            if (checkApiStatus(response)) setWorkingGroups(response.data.data || [])
        })
    }

    const handleItemClicked = (page_type) => trackActivity('Item Clicked', { page_title: 'Invoice Approval Flow', page_type })

    // ── Send for Approval ──────────────────────────────────────────────────────

    const openSendDialog = () => {
        fetchWorkingGroups()
        setSendDialog(true)
    }

    const closeSendDialog = () => {
        setSendDialog(false)
        setSelectedGroup(null)
        setGroupError('')
        setGroupsLoading(false)
    }

    const handleSend = () => {
        if (!selectedGroup) {
            setGroupError('Please select an approval group')
            return
        }
        setSendLoading(true)
        globalPostService(sendInvoiceForApprovalApi(invoiceSlug), { working_group_id: selectedGroup.id })
            .then(response => {
                setSendLoading(false)
                if (checkApiStatus(response)) {
                    enqueueSnackbar(response.data?.message || 'Sent for approval successfully', {
                        variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' }
                    })
                    closeSendDialog()
                    getApprovalDetails()
                } else {
                    enqueueSnackbar(response.data?.message || 'Failed to send for approval', {
                        variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'right' }
                    })
                }
            })
    }

    // ── Approve / Reject ───────────────────────────────────────────────────────

    const openActionDialog = () => {
        dispatch(getStorage({ lease_slug: leaseSlug, module_name: 'invoice_approval' }))
        setActionDialog(true)
    }

    const closeActionDialog = () => {
        setActionDialog(false)
        setRemark('')
        setRemarkError('')
        setAttachments([])
    }

    const handleFileChange = (files) => {
        setAttachments(files)
    }

    const handleUpload = async (slug, mode) => {
        await fileUpload({
            attachments,
            module_name: 'invoice_approval_status_history',
            slug,
            query: { record_action_type: mode },
            onStart: () => setActionLoading(true),
            onComplete: () => setActionLoading(false),
            onSuccess: (success_msg) => {
                enqueueSnackbar(success_msg, { variant: 'success' })
                getApprovalDetails()
            },
            onError: (err) => {
                enqueueSnackbar(err?.message || 'Error while uploading files', { variant: 'error' })
            }
        })
    }

    const handleAction = async (actionStatus) => {
        if (actionStatus === MEMBER_STATUS.REJECTED && !remark.trim()) {
            setRemarkError('Please enter a reason for rejection')
            return
        }
        const approvalSlug = approvalInfo?.userApprovalSlug
        if (!approvalSlug) return

        const loadingKey = actionStatus === MEMBER_STATUS.APPROVED ? 'approve' : 'reject'
        setActionLoading(loadingKey)

        const payload = { status: actionStatus, remark: remark.trim() }

        const actionResponse = await globalPutService(invoiceApprovalActionApi(invoiceSlug, approvalSlug), payload)
        setActionLoading(null)

        if (!checkApiStatus(actionResponse)) {
            enqueueSnackbar(actionResponse.data?.message || 'Action failed', {
                variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'right' }
            })
            return
        }
        trackActivity('Item Edited', { page_title: `Invoice Approval`, success_msg: actionResponse?.data?.message, item_id: payload?.id, payload: payload, response: actionResponse?.data })
        enqueueSnackbar(actionResponse.data?.message || (actionStatus === MEMBER_STATUS.APPROVED ? 'Approved' : 'Rejected'), {
            variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' }
        })

        closeActionDialog()
        getApprovalDetails()

        if (attachments.length) {
            const timelineResponse = await globalGetService(getApprovalFlowimelineApi(invoiceSlug, approvalSlug))
            if (checkApiStatus(timelineResponse)) {
                const timelineData = timelineResponse.data?.data || []
                const statusHistorySlug = timelineResponse.data?.data?.[0]?.slug
                if (statusHistorySlug) {
                    handleUpload(statusHistorySlug,'created')
                }
            }
        }
        
        getApprovalDetails()
        getInvoice()
    }

    // ── Render ─────────────────────────────────────────────────────────────────

    const statusCode = approvalInfo?.statusCode
    const userHasPermission = approvalInfo?.userHasPermission

    if (!approvalInfo || statusCode === STATUS.NOT_ENABLED) return null

    const buttonLabel = (() => {
        switch (statusCode) {
            case STATUS.DRAFT: return 'Send for Approval'
            case STATUS.PENDING: return userHasPermission ? 'Approve / Reject' : 'Pending'
            case STATUS.APPROVED: return 'Approved'
            case STATUS.REJECTED: return 'Send for Re-Approval'
            default: return 'Approval'
        }
    })()

    const isNonDraft = invoiceStatus?.value !== 1
    const isDisabled =
        isNonDraft ||
        statusCode === STATUS.APPROVED ||
        (statusCode === STATUS.PENDING && !userHasPermission)

    const handleButtonClick = () => {
        if (statusCode === STATUS.PENDING && userHasPermission) {
            openActionDialog()
        } else {
            openSendDialog()
        }
    }

    const sendDialogTitle = statusCode === STATUS.REJECTED ? 'Send for Re-Approval' : 'Send for Approval'
    const sendButtonLabel = statusCode === STATUS.REJECTED ? 'Send for Re-Approval' : 'Send for Approval'
    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                    variant='outlined'
                    size='small'
                    disabled={isDisabled}
                    onClick={() => { handleButtonClick(); handleItemClicked(`${buttonLabel} Button`) }}
                    fullWidth
                    sx={
                        statusCode === STATUS.APPROVED ? {
                            color: '#2e7d32',
                            borderColor: '#a5d6a7',
                            backgroundColor: '#f1f8f1',
                            '&.Mui-disabled': { color: '#2e7d32', borderColor: '#a5d6a7', backgroundColor: '#f1f8f1' }
                        } : statusCode === STATUS.PENDING && !userHasPermission && invoiceStatus?.value === 1 ? {
                            color: '#b45309',
                            borderColor: '#fcd34d',
                            backgroundColor: '#fffbeb',
                            '&.Mui-disabled': { color: '#b45309', borderColor: '#fcd34d', backgroundColor:'#fffbeb' }
                        } : {}
                    }
                >
                    {buttonLabel}
                </Button>
                <Tooltip title={"Status TimeLine"} arrow >
                    <RestoreIcon
                        className='revision-history'
                        color='primary'
                        fontSize='small'
                        sx={{ cursor: 'pointer' }}
                        onClick={() => { setTimeLineData({ modal: true, data: approvalInfo }); handleItemClicked('RevisionHistoryIcon') }}
                    />
                </Tooltip>
            </Box>

            {statusCode === STATUS.PENDING && !userHasPermission && (
                <Typography variant='caption' sx={{ color: invoiceStatus?.value === 1 ? '#c1651fbf' : 'GrayText', display: 'block', mt: 0.5, px: 1, py: 0.5, borderRadius: 1 }}>
                    Only members of the assigned working group can change the approval status.
                </Typography>
            )}

            {/* Send for Approval dialog */}
            {sendDialog && (
                <Dialog open maxWidth='xs' fullWidth>
                    <DialogTitle>{sendDialogTitle}</DialogTitle>
                    <DialogContent dividers>
                        <Autocomplete
                            options={workingGroups}
                            getOptionLabel={(option) => option?.name || ''}
                            value={selectedGroup}
                            onChange={(_, val) => { setSelectedGroup(val); setGroupError('') }}
                            disabled={sendLoading}
                            loading={groupsLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    required
                                    label='Approval Group'
                                    margin='normal'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    error={!!groupError}
                                    helperText={groupError}
                                    disabled={sendLoading}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {groupsLoading ? <CircularProgress color='inherit' size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </DialogContent>
                    <DialogActions sx={{ marginRight: '15px' }}>
                        <Button size='small' variant='outlined' onClick={() => { closeSendDialog(); handleItemClicked('Cancel Button - Send for Approval Dialog') }} disabled={sendLoading}>
                            Cancel
                        </Button>
                        <Button size='small' variant='contained' onClick={() => { handleSend(); handleItemClicked(`${sendButtonLabel} Button - Send for Approval Dialog`) }} disabled={sendLoading}>
                            {sendLoading ? <CircularProgress size={18} /> : sendButtonLabel}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Approve / Reject dialog */}
            {actionDialog && (
                <Dialog open maxWidth='sm' fullWidth>
                    <DialogTitle>Approval Action</DialogTitle>
                    <DialogContent dividers>
                        <TextField
                            label='Remark'
                            fullWidth
                            multiline
                            rows={3}
                            margin='normal'
                            variant='outlined'
                            InputLabelProps={{ shrink: true }}
                            value={remark}
                            onChange={(e) => { setRemark(e.target.value); setRemarkError('') }}
                            sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                            error={!!remarkError}
                            helperText={remarkError}
                            disabled={!!actionLoading}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Button disableRipple color='primary' variant='outlined' size='small' sx={{ marginBottom: '10px' }} onClick={() => handleItemClicked('Attachments Button - Approval Action Dialog')}>Attachments</Button>
                            <Button className='available-space-btn' disableRipple size='small' variant='outlined' color='primary'>{`Available Space : ${formatBytes(storageDetails?.remaining_storage)}`}</Button> 
                        </Box>
                        <AttachementsUpload
                            onFileChange={handleFileChange}
                            attachments={[]}
                            remainingStorage={storageDetails?.remaining_storage || 0}
                            isDisabled={!!actionLoading}
                        />
                    </DialogContent>
                    <DialogActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ marginLeft: '15px' }}>
                            <Button
                                size='small'
                                variant='contained'
                                color='primary'
                                disabled={!!actionLoading}
                                onClick={() => handleAction(MEMBER_STATUS.APPROVED)}
                            >
                                {actionLoading === 'approve' ? <CircularProgress size={18} /> : 'Approve'}
                            </Button>
                            <Button
                                size='small'
                                variant='contained'
                                color='error'
                                disabled={!!actionLoading}
                                onClick={() => handleAction(MEMBER_STATUS.REJECTED)}
                                style={{ marginLeft: 8 }}
                            >
                                {actionLoading === 'reject' ? <CircularProgress size={18} /> : 'Reject'}
                            </Button>
                        </div>
                        <Button size='small' sx={{ marginRight: '15px' }} variant='outlined' onClick={() => { closeActionDialog(); handleItemClicked('Cancel Button - Approval Action Dialog') }} disabled={!!actionLoading}>
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {timeLineData?.modal ? <ApprovalTimeline invoiceSlug={invoiceSlug} approvalSlugs={[(approvalInfo?.currentUserMemberSlug || approvalInfo?.anotherUserMemberSlug)].filter(Boolean)} timeLineData={timeLineData} toggleModalFn={() => setTimeLineData({ modal: false })} /> : null}

        </>
    )
}

export default InvoiceApprovalFlow
